import defaultConfig from './default-config.json';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const SUPABASE_URL = 'https://dlmcxxzohervnbixauhs.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRsbWN4eHpvaGVydm5iaXhhdWhzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUxNzcwNzEsImV4cCI6MjA2MDc1MzA3MX0.UIQ7Wqo3R9aTYHYQHTFsHWGdhFFKLHjhEShlz2J2J-Q';
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Generate a tenant ID from the domain or config name
 */
function generateTenantId(domainOrName) {
  // Remove common suffixes and clean up
  return domainOrName
    .replace('.netlify.app', '')
    .replace('.json', '')
    .replace(/[^a-z0-9-]/gi, '-')
    .toLowerCase();
}

/**
 * Fetch PWA icons from database if available
 */
async function fetchPWAIconsFromDatabase(centerCode) {
  try {
    const { data, error } = await supabase
      .from('centers')
      .select('pwa_icons')
      .eq('code', centerCode)
      .single();
    
    if (error || !data || !data.pwa_icons) {
      return null;
    }
    
    return data.pwa_icons;
  } catch (error) {
    console.warn('Failed to fetch PWA icons from database:', error);
    return null;
  }
}

/**
 * Fetch center data including community_features from database
 */
async function fetchCenterDataFromDatabase(centerCode) {
  try {
    console.log('[Config] Fetching center data from database for code:', centerCode);
    
    // Try different variations of the center code
    const codesToTry = [
      centerCode,
      centerCode.toUpperCase(),
      centerCode.toLowerCase(),
      centerCode.replace('-lectures', ''),
      'DEZ' // Fallback to DEZ for Dar-e-Zainab
    ];
    
    for (const code of codesToTry) {
      const { data, error } = await supabase
        .from('centers')
        .select('id, name, code, community_features, zip_code, city, state, country, timezone, hijri_adjustment, google_sheet_id, youtube_channel_url, lecture_clips_enabled, viral_clips_enabled, infographics_enabled, tax_receipts_sheet_id, islamic_occasions_enabled, islamic_occasions_sheet_id, celebration_style, logo_url, splash_logo_url')
        .eq('code', code)
        .single();
      
      if (!error && data) {
        console.log('[Config] Found center data in database:', data);
        return data;
      }
    }
    
    console.warn('[Config] No center data found in database for codes:', codesToTry);
    return null;
  } catch (error) {
    console.warn('Failed to fetch center data from database:', error);
    return null;
  }
}

/**
 * Ensure config has required fields for PWA
 */
async function ensureConfigFields(config, domain) {
  // Generate ID if not present
  if (!config.id) {
    config.id = generateTenantId(domain || config.name || 'default');
  }
  
  // Check if we already have a center_id (UUID) from the GitHub config
  if (config.center_id) {
    console.log('[Config] Found center_id in config:', config.center_id);
    
    // Fetch community_features and location data directly using the UUID
    try {
      const { data, error } = await supabase
        .from('centers')
        .select('community_features, code, name, zip_code, city, state, country, timezone, hijri_adjustment, google_sheet_id, lecture_clips_enabled, viral_clips_enabled, infographics_enabled, tax_receipts_sheet_id, islamic_occasions_enabled, islamic_occasions_sheet_id, celebration_style, logo_url, splash_logo_url')
        .eq('id', config.center_id)
        .single();

      if (!error && data) {
        if (data.community_features) {
          config.community_features = data.community_features;
          console.log('[Config] Loaded community_features from database using center_id UUID');
          console.log('[Config] Payment config loaded:', {
            stripe_enabled: data.community_features?.payment?.stripe?.enabled,
            zelle_enabled: data.community_features?.payment?.zelle?.enabled,
            sponsor_enabled: data.community_features?.announcements?.sponsor_enabled
          });
          console.log('[Config] Services config loaded:', {
            services_exists: !!data.community_features?.services,
            services_enabled: data.community_features?.services?.enabled,
            services_categories_count: data.community_features?.services?.categories?.length
          });
        }
        // Feature flags from database (single source of truth)
        if (data.lecture_clips_enabled !== undefined) config.lecture_clips_enabled = data.lecture_clips_enabled;
        if (data.viral_clips_enabled !== undefined) config.viral_clips_enabled = data.viral_clips_enabled;
        if (data.infographics_enabled !== undefined) config.infographics_enabled = data.infographics_enabled;
        if (data.islamic_occasions_enabled !== undefined) config.islamic_occasions_enabled = data.islamic_occasions_enabled;
        if (data.islamic_occasions_sheet_id) config.islamic_occasions_sheet_id = data.islamic_occasions_sheet_id;
        if (data.celebration_style) config.celebration_style = data.celebration_style;
        // Add location fields to config
        if (data.zip_code) config.zip_code = data.zip_code;
        if (data.city) config.city = data.city;
        if (data.state) config.state = data.state;
        if (data.country) config.country = data.country;
        if (data.timezone) {
          config.timezone = data.timezone;
          console.log('[Config] Loaded timezone from database:', data.timezone);
        }
        if (data.hijri_adjustment !== undefined) {
          config.hijri_adjustment = data.hijri_adjustment;
          console.log('[Config] Loaded hijri_adjustment from database:', data.hijri_adjustment);
        }
        // CRITICAL: Override google_sheet_id from Supabase (single source of truth)
        if (data.google_sheet_id) {
          const oldSheetId = config.google_sheet_id;
          config.google_sheet_id = data.google_sheet_id;
          console.log('[Config] ✅ Loaded google_sheet_id from Supabase database:', data.google_sheet_id);
          if (oldSheetId && oldSheetId !== data.google_sheet_id) {
            console.log('[Config] ⚠️ Overriding config file sheet ID:', oldSheetId, '→', data.google_sheet_id);
          }
        }
        // Tax receipts sheet ID (presence enables Tax Docs feature)
        if (data.tax_receipts_sheet_id) {
          config.tax_receipts_sheet_id = data.tax_receipts_sheet_id;
        }
        // Logo URLs: Supabase wins over GitHub config so admins can swap logos
        // without re-deploying or pushing a config commit. logo_url drives both
        // the header logo and (via PWA icon regeneration) the home-screen icon.
        // splash_logo_url drives ONLY the in-app boot splash and is optional —
        // falls back to logo_url in src/index.js if not set.
        if (data.logo_url) {
          config.logo_url = data.logo_url;
          console.log('[Config] Loaded logo_url from Supabase:', data.logo_url);
        }
        if (data.splash_logo_url) {
          config.splash_logo_url = data.splash_logo_url;
          console.log('[Config] Loaded splash_logo_url from Supabase:', data.splash_logo_url);
        }
      } else {
        console.warn('[Config] Failed to fetch center data by UUID:', error);
      }
    } catch (err) {
      console.error('[Config] Error fetching center data by UUID:', err);
    }
  } else {
    // Fallback to the original code-based search if no center_id
    const centerData = await fetchCenterDataFromDatabase(config.id);
    if (centerData) {
      // Merge database data with config
      if (centerData.community_features) {
        config.community_features = centerData.community_features;
        console.log('[Config] Merged community_features from database using code search');
      }
      
      // Use the database UUID as center_id for queries
      if (centerData.id) {
        config.center_id = centerData.id;
        console.log('[Config] Set center_id from database:', config.center_id);
      }

      // Feature flags from database (single source of truth)
      if (centerData.lecture_clips_enabled !== undefined) config.lecture_clips_enabled = centerData.lecture_clips_enabled;
      if (centerData.viral_clips_enabled !== undefined) config.viral_clips_enabled = centerData.viral_clips_enabled;
      if (centerData.infographics_enabled !== undefined) config.infographics_enabled = centerData.infographics_enabled;
      if (centerData.islamic_occasions_enabled !== undefined) config.islamic_occasions_enabled = centerData.islamic_occasions_enabled;
      if (centerData.islamic_occasions_sheet_id) config.islamic_occasions_sheet_id = centerData.islamic_occasions_sheet_id;
      if (centerData.celebration_style) config.celebration_style = centerData.celebration_style;

      // Add location fields from database
      if (centerData.zip_code) config.zip_code = centerData.zip_code;
      if (centerData.city) config.city = centerData.city;
      if (centerData.state) config.state = centerData.state;
      if (centerData.country) config.country = centerData.country;
      if (centerData.timezone) {
        config.timezone = centerData.timezone;
        console.log('[Config] Loaded timezone from database (fallback):', centerData.timezone);
      }
      if (centerData.hijri_adjustment !== undefined) {
        config.hijri_adjustment = centerData.hijri_adjustment;
        console.log('[Config] Loaded hijri_adjustment from database (fallback):', centerData.hijri_adjustment);
      }
      // CRITICAL: Override google_sheet_id from Supabase (single source of truth)
      if (centerData.google_sheet_id) {
        const oldSheetId = config.google_sheet_id;
        config.google_sheet_id = centerData.google_sheet_id;
        console.log('[Config] ✅ Loaded google_sheet_id from Supabase database (fallback):', centerData.google_sheet_id);
        if (oldSheetId && oldSheetId !== centerData.google_sheet_id) {
          console.log('[Config] ⚠️ Overriding config file sheet ID:', oldSheetId, '→', centerData.google_sheet_id);
        }
      }
      // Tax receipts sheet ID (presence enables Tax Docs feature)
      if (centerData.tax_receipts_sheet_id) {
        config.tax_receipts_sheet_id = centerData.tax_receipts_sheet_id;
      }
      // Logo URLs (see comment above for the UUID branch)
      if (centerData.logo_url) {
        config.logo_url = centerData.logo_url;
        console.log('[Config] Loaded logo_url from Supabase (fallback):', centerData.logo_url);
      }
      if (centerData.splash_logo_url) {
        config.splash_logo_url = centerData.splash_logo_url;
        console.log('[Config] Loaded splash_logo_url from Supabase (fallback):', centerData.splash_logo_url);
      }
    }
  }

  // Log final center_id status
  if (config.center_id) {
    console.log('[Config] Final center_id:', config.center_id);
  }
  
  // Ensure short_name if not present — use full name, no truncation
  if (!config.short_name) {
    config.short_name = config.name || 'App';
  }
  
  // Try to fetch PWA icons from database if not in config
  if (!config.pwa_icons && config.id) {
    const dbIcons = await fetchPWAIconsFromDatabase(config.id);
    if (dbIcons) {
      config.pwa_icons = dbIcons;
      console.log('Loaded PWA icons from database for:', config.id);
      console.log('[Config] PWA icons loaded:', Object.keys(dbIcons));
    } else {
      console.log('[Config] No PWA icons found in database for:', config.id);
    }
  }
  
  return config;
}

/**
 * Load configuration for the current center
 */
export async function loadConfig() {
  // Check for injected config first (from edge function) to avoid redundant fetches
  const injectedConfig = document.getElementById('__TENANT_CONFIG__');
  if (injectedConfig) {
    try {
      const config = JSON.parse(injectedConfig.textContent || '{}');
      if (config && Object.keys(config).length > 0) {
        console.log('[Config] Using injected config from edge function:', config);
        const finalConfig = await ensureConfigFields(config, window.location.hostname);
        localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
        return finalConfig;
      }
    } catch (error) {
      console.warn('[Config] Failed to parse injected config:', error);
    }
  }

  const domain = window.location.hostname;
  const repoOwner = 'usthrive'; // Define GitHub repo owner
  const repoName = 'center-configs'; // Define GitHub repo name
  const branch = 'main'; // Define GitHub branch

  // -------------------------------------------------------------
  // Persist selected ?domain parameter in sessionStorage so that
  // client-side SPA navigation continues using the same config.
  // -------------------------------------------------------------
  const SESSION_KEY = 'dez_selected_domain';

  // Helper: attempt to get persisted domain
  const persistedDomainParam = typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEY) : null;

  // Determine environment and load config accordingly

  // v2: Fetch from public folder
  // --- Local Development Logic --- 
  if (domain.includes('localhost') || domain.includes('ngrok-free.app')) {
    console.log('[DEV] Detected localhost environment.');
    const params = new URLSearchParams(window.location.search);
    const targetDomainParam = params.get('domain') || params.get('config');
    const persistedDomain = typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEY) : null;
    let effectiveDomain = targetDomainParam || persistedDomain;

    if (targetDomainParam) {
      if (typeof window !== 'undefined') sessionStorage.setItem(SESSION_KEY, targetDomainParam);
    }
    
    // Determine the config file name. Use a default if none is specified (e.g., 'dez.json').
    const configFileName = effectiveDomain ? `${effectiveDomain}.json` : 'dez.json'; 
    console.log(`[DEV] Effective domain for config: ${effectiveDomain || '(defaulting to dez.json)'}, target file: ${configFileName}`);

    // 1. Attempt to fetch from GitHub directly
    const githubUrl = `https://raw.githubusercontent.com/usthrive/center-configs/main/configs/${configFileName}`;
    const cacheBuster = `?t=${Date.now()}`;
    console.log(`[DEV] Attempting to fetch from GitHub: ${githubUrl}${cacheBuster}`);
    try {
        const ghResponse = await fetch(`${githubUrl}${cacheBuster}`, { cache: 'no-store' });
        if (ghResponse.ok) {
            const data = await ghResponse.json();
            console.log('[DEV] Successfully loaded config from GitHub:', data);
            const finalConfig = await ensureConfigFields(data, effectiveDomain);
            // Cache config in localStorage for meta tag service
            localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
            return finalConfig;
        }
        console.warn(`[DEV] Failed to fetch from GitHub ${githubUrl}. Status: ${ghResponse.status}. Trying local /test-configs/ fallback.`);
    } catch (ghError) {
        console.warn(`[DEV] Error fetching from GitHub:`, ghError, `. Trying local /test-configs/ fallback.`);
    }

    // 2. Fallback: Attempt to fetch from local /test-configs/
    const localTestUrl = `/test-configs/${configFileName}`;
    console.log(`[DEV] Attempting to fetch from local /test-configs/: ${localTestUrl}`);
    try {
        const localResponse = await fetch(localTestUrl, { cache: 'no-cache' });
        if (localResponse.ok) {
            const data = await localResponse.json();
            console.log('[DEV] Successfully loaded config from local /test-configs/:', data);
            const finalConfig = await ensureConfigFields(data, effectiveDomain);
            // Cache config in localStorage for meta tag service
            localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
            return finalConfig;
        }
        console.warn(`[DEV] Failed to fetch from local /test-configs/ ${localTestUrl}. Status: ${localResponse.status}. Using hardcoded default.`);
    } catch (localError) {
        console.warn(`[DEV] Error fetching from local /test-configs/:`, localError, `. Using hardcoded default.`);
    }
    
    // 3. Final Fallback: Use hardcoded default config
    console.log('[DEV] Using hardcoded default config.');
    const finalConfig = await ensureConfigFields(defaultConfig, 'default');
    // Cache config in localStorage for meta tag service
    localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
    return finalConfig;
  }
  
  // --- Production Logic --- 
  else {
    console.log('[PROD] Detected production environment.');
    const domain = window.location.hostname; // e.g., alkhoei-lectures.netlify.app
    const configFileNameOnGitHub = `${domain}.json`; // e.g., alkhoei-lectures.netlify.app.json

    // Construct the direct GitHub raw content URL
    const configUrl = `https://raw.githubusercontent.com/usthrive/center-configs/main/configs/${configFileNameOnGitHub}`;
    const cacheBuster = `?t=${Date.now()}`;
    
    console.log(`[PROD] Attempting to fetch config directly from GitHub: ${configUrl}${cacheBuster}`);

    try {
      const response = await fetch(`${configUrl}${cacheBuster}`, { cache: 'no-store' });

      if (response.ok) {
        const data = await response.json();
        console.log('[PROD] Successfully loaded config from GitHub:', data);
        const finalConfig = await ensureConfigFields(data, domain);
        // Cache config in localStorage for meta tag service
        localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
        return finalConfig;
      } else {
        console.warn(`[PROD] Failed to fetch config from GitHub ${configUrl}. Status: ${response.status}. Falling back to default.`);
        const finalConfig = await ensureConfigFields(defaultConfig, 'default'); // Fallback
        // Cache config in localStorage for meta tag service
        localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
        return finalConfig;
      }
    } catch (error) {
      console.error('[PROD] Error fetching config from GitHub:', error);
      const finalConfig = await ensureConfigFields(defaultConfig, 'default'); // Fallback
      // Cache config in localStorage for meta tag service
      localStorage.setItem('centerConfig', JSON.stringify(finalConfig));
      return finalConfig;
    }
  }
}

// Create a singleton promise for React to use
const configPromise = loadConfig();
export default configPromise;
